//
//  settingsPages.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the guides of the settings pages other than simple lookups.
//

import { Permission } from "src/api/Tmd.dtos";

import {
    builderTourTargets,
    helpTourTargets,
    hubspotTourTargets,
    insertionTourTarget
} from "../helpTourTargets";
import { builderStops, emailRecipientsGuide } from "./commonGuides";
import { lookupCreateGuide, lookupItemGuide, lookupListGuide } from "./lookupGuides";
import {
    actionsStop,
    cards,
    column,
    covering,
    createGuide,
    field,
    filterStop,
    grid,
    item,
    menu,
    saveStop,
    stop,
    type PageGuide,
    type PageGuideStop
} from "./pageGuide";

type Catalogue = {
    list: string;
    item: string;
    create: string;
    plural: string;
    singular: string;
    purpose: string;
    categoryPath?: string;
    createPermission: Permission;
    updatePermission: Permission;
    effect: string;
};

/** Guides for a colour-coded category list (capability, tag and participant tag categories). */
const categoryGuides = (catalogue: Catalogue): Record<string, PageGuide> => {
    const edit: PageGuide = {
        intro: `Change one ${catalogue.singular}. ${catalogue.purpose}`,
        stops: [
            field(
                "name",
                "Name",
                `The heading ${catalogue.plural} are grouped under wherever they are listed.`
            ),
            field("rank", "Rank", "The order of the categories; lower ranks come first."),
            field(
                "colour",
                "Colour",
                "The colour used for the badges of everything in this category, so it can be recognised at a glance. Choose colours that stay distinct from the other categories."
            ),
            field(
                "active",
                "Active",
                "Clear to retire the category. Items in it keep it, but it cannot be chosen for new or changed items."
            ),
            saveStop(
                "Submit saves; Submit And Return saves and goes back to the list.",
                catalogue.updatePermission
            )
        ],
        notes: [catalogue.effect]
    };

    return {
        [catalogue.list]: {
            intro: `The ${catalogue.plural}. ${catalogue.purpose}`,
            stops: [
                filterStop(
                    "Type to filter by name; switch on inactive records to include retired categories."
                ),
                actionsStop(
                    `Add opens a blank ${catalogue.singular}; nothing is saved until you submit it.`,
                    catalogue.createPermission
                ),
                grid(`Select a row to change a category. Greyed rows are inactive.`, [
                    column(
                        "Name",
                        "The category's name, shown as the heading its entries are grouped under."
                    ),
                    column("Colour", "The badge colour used for everything in the category.")
                ])
            ]
        },
        [catalogue.item]: edit,
        [catalogue.create]: createGuide(edit, `Add a ${catalogue.singular}. ${catalogue.purpose}`, [
            "Once saved, the category can be chosen for new entries straight away."
        ])
    };
};

/** Guides for a categorised catalogue (capabilities, institution tags and participant tags). */
const catalogueGuides = (catalogue: Catalogue): Record<string, PageGuide> => {
    const edit: PageGuide = {
        intro: `Change one ${catalogue.singular}. ${catalogue.purpose}`,
        stops: [
            field(
                "name",
                "Name",
                `The name trials choose from. Names must be unique across all ${catalogue.plural}.`
            ),
            field(
                catalogue.categoryPath ?? "categoryId",
                "Category",
                `The category it is grouped under, which also decides its colour. An inactive category can be kept but not newly chosen.`
            ),
            field(
                "description",
                "Description",
                `Explain what having this ${catalogue.singular} means, so trials choose it consistently.`
            ),
            field("rank", "Rank", "The order within its category; lower ranks come first."),
            field(
                "active",
                "Active",
                `Clear to retire it. Trials that already use it keep it; it cannot be newly chosen.`
            ),
            saveStop(
                "Submit saves; Submit And Return saves and goes back to the list.",
                catalogue.updatePermission
            )
        ],
        notes: [catalogue.effect]
    };

    return {
        [catalogue.list]: {
            intro: `The catalogue of ${catalogue.plural}. ${catalogue.purpose}`,
            stops: [
                filterStop(
                    "Type to filter by name, category or description; switch on inactive records to include retired entries."
                ),
                actionsStop(
                    `Add opens a blank ${catalogue.singular}; nothing is saved until you submit it.`,
                    catalogue.createPermission
                ),
                grid(`Select a row to change an entry. Greyed rows are inactive.`, [
                    column(
                        "Name",
                        "The name trials choose from, as shown on sites and participants."
                    ),
                    column("Category", "The category it is grouped and coloured by."),
                    column("Description", "What having it means, so trials choose it consistently.")
                ])
            ]
        },
        [catalogue.item]: edit,
        [catalogue.create]: createGuide(
            edit,
            `Add a ${catalogue.singular} to the catalogue. ${catalogue.purpose}`,
            [`Trials can choose the new ${catalogue.singular} as soon as it is saved.`],
            { omit: ["active"] }
        )
    };
};

/** Guides for a library item template (trial, trial institution and participant templates). */
const templateGuides = (
    routes: { list: string; item: string; create: string },
    options: {
        plural: string;
        singular: string;
        purpose: string;
        versionPath: "allowVersions" | "allowIntraVersions";
        /** Whether the template page shows a rank. */
        withRank: boolean;
        createPermission: Permission;
        updatePermission: Permission;
    }
): Record<string, PageGuide> => {
    const edit: PageGuide = {
        intro: `Change a library ${options.singular}. ${options.purpose}`,
        stops: [
            field(
                "name",
                "Name",
                "The item name people see in trials once the template is imported."
            ),
            field(
                "categoryId",
                "Category",
                "Groups the template in lists and in the import screens."
            ),
            field(
                "description",
                "Description",
                "What the item collects and when; shown to people choosing templates to import."
            ),
            ...(options.withRank
                ? [
                      field(
                          "rank",
                          "Rank",
                          "The order the template is listed in within its category; lower numbers are listed first."
                      )
                  ]
                : []),
            field(
                "active",
                "Active",
                "Clear to stop the template being offered for import. Copies already in trials are unaffected."
            ),
            field(
                options.versionPath,
                options.versionPath === "allowVersions" ? "Allow Versions" : "Allow Intra Versions",
                "Whether items created from this template can be versioned, so a new version can be recorded while the earlier ones are kept."
            ),
            field(
                "requiresReview",
                "Requires Review",
                "When enabled, submitted items wait for review before being marked Complete. Otherwise, they become Complete on submission."
            ),
            field(
                "initialVersion",
                "Initial Version",
                "The version number given to a newly created item, such as 1.0."
            ),
            ...builderStops(options.updatePermission),
            saveStop(
                "Submit saves the template; Submit And Return saves and goes back to the list.",
                options.updatePermission
            )
        ],
        notes: [
            "Changing the library template does not change copies already imported into trials; re-import or change the trial's copy for that."
        ]
    };

    return {
        [routes.list]: {
            intro: `The library of ${options.plural}. ${options.purpose}`,
            stops: [
                filterStop(
                    "Type to filter by name, category or description; switch on inactive records to include retired templates."
                ),
                actionsStop(
                    `Add opens a blank ${options.singular}; build its layout once it is saved.`,
                    options.createPermission
                ),
                grid("Each row is one template; select it to change it and its layout.", [
                    column("Category", "The item category the template is grouped under."),
                    column("Name", "The item name trials see once they import it."),
                    column("Description", "What the template's items collect and when.")
                ])
            ]
        },
        [routes.item]: edit,
        [routes.create]: createGuide(
            edit,
            `Add a ${options.singular} to the library. ${options.purpose}`,
            [
                "Submit creates the template and opens it; build its layout there, then make it available by leaving it active.",
                "Trials only get the template when they import it."
            ]
        )
    };
};

const emailTemplate: PageGuide = {
    intro: "Write an email template: its type, subject and body. Merge fields insert details of the trial, institution, record and recipients when the email is sent. Preview shows it with sample values; the overdue reminder can also be previewed against a real form.",
    stops: [
        field(
            "standardEmailTypeId",
            "Type",
            "Which email this template is used for, such as the overdue form reminder or an item's assigned email. It decides which merge fields are offered."
        ),
        field(
            "subject",
            "Email Subject",
            "The subject line. Type {{ or choose Insert replacement field to add details such as the trial or form name. Replacement fields appear as chips and are filled in when the email is sent."
        ),
        field(
            "emailImportance",
            "Email Importance",
            "Choose Low, Normal or High importance for outgoing emails. Normal is the default."
        ),
        field(
            "emailLayout",
            "Email Layout",
            "Choose Trial Management Layout for the TROG design or Central Quality Management Layout for the CQMS design. Both use blue with orange accents. The CQMS signature and contact use the configured sender identity."
        ),
        field(
            "body",
            "Body",
            "The message. Type {{ to insert a merge field, such as the trial display name, form name or recipient names; each is replaced with the real value in every email sent."
        ),
        field("value", "Name", "The template's name, shown in the list of QA email templates."),
        field(
            "rank",
            "Rank",
            "The order the template is listed in; lower numbers are listed first."
        ),
        field(
            "active",
            "Active",
            "Clear to stop the template being used; the email then falls back to its default wording."
        ),
        stop(
            helpTourTargets.pageActions,
            "Previewing",
            "Preview opens a panel: choose a form template and an actual form, then Render preview shows the email as it would be sent for that form, with its merge fields filled in.",
            Permission.QAEmailTemplatesUpdate
        ),
        saveStop(
            "Submit saves the template; emails sent afterwards use it.",
            Permission.QAEmailTemplatesUpdate
        )
    ]
};

const componentBuilder = (singular: string, usedIn: string, permission: Permission): PageGuide => ({
    intro: `Build one ${singular}: a reusable question or input used in ${usedIn}.`,
    stops: [
        field("name", "Name", `The component name, shown when adding it to ${usedIn}.`),
        field(
            "description",
            "Description",
            "What the component asks for; helps others reuse it correctly."
        ),
        field(
            "itemComponentTypeId",
            "Component Type",
            "The kind of form or item the component is for. It decides where the component can be used."
        ),
        field(
            "customInputControlName",
            "Custom Input Control Name",
            "The name of a purpose-built input to use instead of the standard inputs, for components that need one. Leave empty normally."
        ),
        field(
            "active",
            "Active",
            `Clear to stop it being added to ${usedIn}; places that already use it keep it.`
        ),
        covering(
            field(
                "sourceDocuments",
                "Source Documents",
                "Reference documents shown with the component, such as instructions. Add a row per document: name it, choose its file type and upload the file."
            ),
            ["name", "Name"],
            ["fileType", "File Type"],
            ["fileInformation", "File"]
        ),
        covering(
            field(
                "itemComponentRules",
                "Item Component Rules",
                "Rules that make an input depend on another: choose the input (Primitive), the Rule to apply, and the other input and value that trigger it (When Other Primitive, Has Value)."
            ),
            ["itemComponentPrimitiveId", "Primitive"],
            ["itemPrimitiveRule", "Rule"],
            ["compareItemComponentPrimitiveId", "When Other Primitive"],
            ["compareValue", "Has Value"]
        ),
        field(
            "itemComponentPrimitives",
            "Item Component Primitives",
            "The inputs the component collects, such as a date, a choice or a number, each with its label, options and whether it is required."
        ),
        stop(
            insertionTourTarget("Add primitive here"),
            "Adding an input",
            "The + between inputs inserts a new input at that point."
        ),
        stop(
            builderTourTargets.primitives,
            "Inputs",
            "Each input's settings: its label, options, whether it is required and how its value is checked."
        ),
        stop(
            helpTourTargets.pageActions,
            "Clone",
            "Clone copies the component into a new one, so a variation can be made without changing the templates that use this one.",
            permission
        ),
        saveStop(
            "Submit saves the component; every template that uses it picks up the change.",
            permission
        )
    ],
    notes: [
        `Changes affect every ${usedIn.replace(/s$/, "")} that uses the component. Clone a component instead when only one template needs a variation.`
    ]
});

/** The fields a role has beyond a lookup's name, rank and active state. */
const roleFields: PageGuideStop[] = [
    field(
        "description",
        "Description",
        "What the role is for, shown to people choosing a role for personnel."
    ),
    field(
        "isSystem",
        "Is System",
        "Marks a built-in role the application relies on. Only people who can update system roles can change it or its permissions."
    ),
    field(
        "openClinciaRoleId",
        "Open Clinica Trial Role",
        "The OpenClinica study role created for people holding this role on a trial. Leave empty for no trial-level OpenClinica access."
    ),
    field(
        "openClinciaInstitutionRoleId",
        "Open Clinica Institution Role",
        "The OpenClinica site role created for people holding this role at a trial institution."
    ),
    field(
        "opmsAccess",
        "OPMS Access",
        "Whether people holding the role get access to the OPMS online participant management system."
    ),
    field(
        "saeRoleId",
        "SAE Role",
        "The role people holding this role get in the serious adverse event system, if any."
    ),
    field(
        "drugManagementAccess",
        "Drug Management Access",
        "Whether people holding the role get access to drug management."
    )
];

const roleLookup = {
    plural: "roles",
    singular: "role",
    purpose:
        "A role is what personnel are given on a trial, institution, department or group; its permissions decide what they can see and do there.",
    usedIn: "the Role field when personnel are given a role",
    createPermission: Permission.RolesCreate,
    updatePermission: Permission.RolesUpdate,
    extraFields: roleFields
};

const settingsPageGuides: Record<string, PageGuide> = {
    "/settings": {
        intro: "Settings holds the reference data, templates and defaults shared by the whole site. Each card opens one area; you only see the areas you can manage.",
        stops: [
            stop(
                helpTourTargets.breadcrumbs,
                "Getting back",
                "Use the breadcrumb trail to return to this page from any area."
            )
        ],
        notes: [
            "Changes in settings affect every trial, institution or person that uses the value. Check with the owner of the data before changing shared values."
        ]
    },

    // Roles and permissions
    "/settings/general/roles": {
        ...lookupListGuide(roleLookup),
        notes: [
            "Hover a role's permission counts to see them by scope; Details opens the full list of the role's permissions."
        ]
    },
    "/settings/general/roles/:roleId/details": (() => {
        const guide = lookupItemGuide({
            ...roleLookup,
            purpose: "The role's permissions are in the Permissions section of the side menu."
        });
        return {
            ...guide,
            stops: [
                ...guide.stops,
                menu(
                    item(
                        "Details",
                        "This page: the role's name, description and linked system roles."
                    ),
                    item(
                        "Permissions",
                        "The permissions the role grants, each at a scope and optionally only for certain trials, statuses or institutions."
                    )
                )
            ]
        };
    })(),
    "/settings/general/roles/create": lookupCreateGuide({
        ...roleLookup,
        purpose: "Add its permissions from the Permissions section once it is saved."
    }),
    "/settings/general/roles/:roleId/permissions": {
        intro: "The permissions this role grants. Each row grants one permission, in one module, at a scope, optionally only for certain trials, statuses or institutions.",
        stops: [
            filterStop("Type to filter by module, permission group or permission."),
            actionsStop(
                "Add Permissions grants more permissions to the role, several at a time.",
                Permission.RolesUpdateSystem
            ),
            grid("Select a row to see the full requirement or remove it.", [
                column("Module", "The application the permission applies to, such as TMD."),
                column(
                    "Permission Group",
                    "The area the permission belongs to, such as Trials or Notices."
                ),
                column("Permission", "What the permission allows.")
            ])
        ],
        notes: [
            "Changes apply to everyone who holds the role when their permissions are next loaded."
        ]
    },
    "/settings/general/roles/:roleId/permissions/create": {
        intro: "Grant permissions to this role. Choose the permissions, then where they apply.",
        stops: [
            field(
                "permissionIds",
                "Permissions",
                "One or more permissions to grant, grouped by area. Each becomes its own row with the settings below."
            ),
            field(
                "moduleId",
                "Module",
                "The application the permissions apply to; TMD for this site."
            ),
            field(
                "scope",
                "Scope",
                "Where the permission applies. Global: everywhere. Trial: on the trials where the person holds the role. Institution: at the institutions where they hold it. Trial Institution: only for that trial at that institution. Patient: for particular participants."
            ),
            field(
                "matchType",
                "Match Type",
                "How a person is matched to the role. TMD Match uses the roles recorded in this site; the other types match through group, committee group or participant associations."
            ),
            field(
                "trialId",
                "Trial",
                "Optional. Limit the grant to one trial rather than every trial where the person holds the role."
            ),
            field(
                "trialStatusId",
                "Trial Status",
                "Optional. Only grant it while the trial is at this status, for example to stop edits once a trial is closed."
            ),
            field(
                "institutionCode",
                "Institution",
                "Optional. Limit the grant to one institution."
            ),
            field(
                "trialInstitutionStatusId",
                "Trial Institution Status",
                "Optional. Only grant it while the site is at this status."
            ),
            field(
                "patientStudyNumber",
                "Patient Study Number",
                "Optional. Limit the grant to one participant."
            ),
            saveStop("Submit adds the permissions to the role.", Permission.RolesUpdateSystem)
        ]
    },
    "/settings/general/roles/:roleId/permissions/:permissionRequirementRoleId": {
        intro: "One permission this role grants and the conditions it applies under. The settings are read-only; remove the grant and add it again to change them.",
        stops: [
            field(
                "permissionId",
                "Permission",
                "The permission granted: what people holding the role are allowed to see or do."
            ),
            field(
                "moduleId",
                "Module",
                "The application the permission applies to; TMD for this system."
            ),
            field(
                "scope",
                "Scope",
                "Where it applies: globally, or on the trials, institutions or trial institutions where the person holds the role, or for particular participants."
            ),
            field(
                "matchType",
                "Match Type",
                "How a person is matched to the role, such as through the roles recorded in this system."
            ),
            field(
                "trialId",
                "Trial",
                "When set, the grant applies only on this trial rather than every trial where the person holds the role."
            ),
            field(
                "trialStatusId",
                "Current Status (TROG)",
                "When set, the grant applies only while the trial is at this status, for example to stop edits once a trial is closed."
            ),
            field(
                "institutionCode",
                "Institution",
                "When set, the grant applies only at this institution."
            ),
            field(
                "trialInstitutionStatusId",
                "Status",
                "When set, the grant applies only while the trial institution is at this status."
            ),
            field(
                "patientStudyNumber",
                "Patient Study Number",
                "When set, the grant applies only for this participant."
            ),
            saveStop(
                "Remove takes the permission away from everyone with this role; Cancel goes back without changing anything.",
                Permission.RolesUpdateSystem
            )
        ]
    },

    // Notices
    "/settings/general/notices": {
        intro: "Notices are messages shown to users, such as planned maintenance or new features. Each has a display style, severity, audience, pages and schedule.",
        stops: [
            filterStop(
                "Type to filter by title or message; switch on inactive records to include retired notices."
            ),
            actionsStop(
                "Add opens a blank notice; it is shown from its start date once saved.",
                Permission.NoticesCreate
            ),
            grid("Each row is one notice; select it to change or withdraw it.", [
                column("Title", "The notice's heading, as shown to users."),
                column(
                    "Display",
                    "Dashboard Only, a Banner at the top of pages, or a Modal that must be acknowledged."
                ),
                column(
                    "Page Visibility",
                    "All pages, or only the pages of the records chosen on the notice."
                ),
                column("Severity", "The colour and icon, from information to critical."),
                column("Starts", "When the notice starts showing."),
                column(
                    "Ends",
                    "When it stops showing; notices without an end date stay until deactivated."
                )
            ])
        ]
    },
    "/settings/general/notices/:noticeId": {
        intro: "Change a notice: what it says, how it is displayed, who sees it, on which pages, and when.",
        stops: [
            field(
                "title",
                "Title",
                "A short heading, shown in bold above the message and in the notices list."
            ),
            field("message", "Message", "The notice text. Formatting and links are allowed."),
            field(
                "displayOptionId",
                "Display",
                "Dashboard Only shows it on the dashboard; Banner shows it at the top of pages; Modal pops it up until it is acknowledged."
            ),
            field(
                "noticeSeverityId",
                "Severity",
                "Sets the colour and icon. Use Critical only for outages and urgent actions."
            ),
            field(
                "dismissible",
                "Can be acknowledged",
                "Let each user close the notice once they have read it. Acknowledged notices stay closed for that user."
            ),
            field(
                "startDate",
                "Start Date",
                "When the notice starts showing; set a future date to schedule it in advance."
            ),
            field(
                "endDate",
                "End Date",
                "Optional. When it stops showing; it must be after the start date."
            ),
            covering(
                field(
                    "audienceOptionId",
                    "Audience",
                    "Everyone, or only people with selected roles, on selected trials or at selected institutions or trial institutions, or selected people. Choose them in the lists that appear."
                ),
                ["roles", "Roles"],
                ["trials", "Trials"],
                ["institutions", "Institutions"],
                ["trialInstitutions", "Trial Institutions"],
                ["personnel", "Personnel"]
            ),
            covering(
                field(
                    "pageVisibilityOptionId",
                    "Page Visibility",
                    "All pages, or only the pages of selected groups, trials, institutions, trial institutions, participants or personnel. Choose them in the lists that appear."
                ),
                ["pageGroups", "Groups"],
                ["pageTrials", "Trials"],
                ["pageInstitutions", "Institutions"],
                ["pageTrialInstitutions", "Trial Institutions"],
                ["pagePatients", "Participants"],
                ["pagePersonnel", "Personnel"]
            ),
            field("active", "Active", "Clear to withdraw the notice immediately."),
            saveStop(
                "Submit saves the notice; users see the change the next time their notices refresh.",
                Permission.NoticesUpdate
            )
        ],
        rules: ["Dashboard Only notices must use All Pages visibility; the form sets this for you."]
    },

    "/settings/general/emails": emailRecipientsGuide(
        "global",
        "The standard recipients for every email the system sends. Every trial uses these until it sets its own; sites, participants and case reports can then override the trial.",
        Permission.EmailRecipientDefaultsUpdate
    ),
    "/settings/general/help": {
        intro: "Tracks help for every page: whether its help text is missing, a draft or written, and whether it has a tour. Edit any page's help from here.",
        stops: [
            filterStop(
                "Filter by page, route, area or status, for example Draft or Missing. Switch on inactive records to include pages that share another page's help."
            ),
            grid("Each row is one page; select it to edit the page's help.", [
                column(
                    "Area",
                    "The part of the site the page belongs to, such as Trials or Settings."
                ),
                column("Page", "The page's name, as shown in its heading."),
                column(
                    "Route",
                    "The page's address pattern, which the help is stored against; create pages have their own."
                ),
                column(
                    "Help Text",
                    "Missing, Draft (seeded and not yet reviewed), Written, Inactive, Shared (uses another page's help) or Not needed."
                ),
                column("Sections", "Active sections out of all sections."),
                column(
                    "Access",
                    "Who the Help page lists the page's help for: everyone, or only users who hold the permission shown, or any one or all of several (hover to see them), somewhere."
                ),
                column("Tour", "Whether the page has its own tour. Tours are added by developers.")
            ])
        ]
    },
    "/settings/hubspot/activemembers": {
        intro: "Every HubSpot member, active and inactive, and how each is matched to a person in this system.",
        stops: [
            stop(
                hubspotTourTargets.summary,
                "Summary",
                "Counts of active and inactive members, members matched to personnel, new active members not yet matched, and conflicts."
            ),
            stop(
                helpTourTargets.pageActions,
                "Process membership",
                "Process membership matches members to personnel and updates their membership, then reports how many were updated, need review or failed.",
                Permission.HubspotContactExportCreate
            ),
            grid(
                "Each row is one HubSpot member, active or inactive, with how it matches personnel.",
                [
                    column("HubSpot member", "The member's name in HubSpot."),
                    column("Email", "The member's email address in HubSpot."),
                    column("Contact type", "The kind of HubSpot contact, such as a member."),
                    column("Membership", "The member's TROG membership type and state in HubSpot."),
                    column(
                        "Personnel",
                        "The person in this system the member is matched to, if any; select it to open their HubSpot page."
                    ),
                    column(
                        "Matched by",
                        "How the member was matched to the person, such as by email or HubSpot identity."
                    ),
                    column(
                        "Issue",
                        "Why the member needs attention, such as an email difference or a conflicting match."
                    )
                ]
            )
        ]
    },
    "/settings/hubspot/import": {
        intro: "Active HubSpot members who are not yet matched to a person in this system.",
        stops: [
            stop(
                hubspotTourTargets.summary,
                "Summary",
                "Counts of active and inactive members, matched personnel, new active members and conflicts, across all members."
            ),
            grid("Each row is one new active member without a matching person.", [
                column("HubSpot member", "The member's name in HubSpot."),
                column("Email", "The member's email address in HubSpot."),
                column("Contact type", "The kind of HubSpot contact, such as a member."),
                column("Membership", "The member's TROG membership type and state in HubSpot."),
                column(
                    "Personnel",
                    "The person in this system the member is matched to, if any; select it to open their HubSpot page."
                ),
                column(
                    "Matched by",
                    "How the member was matched to the person, such as by email or HubSpot identity."
                ),
                column(
                    "Issue",
                    "Why the member needs attention, such as an email difference or a conflicting match."
                )
            ])
        ],
        notes: ["Process membership, on the Active Members page, matches new members to personnel."]
    },
    "/settings/hubspot/emailupdate": {
        intro: "Members whose email differs from their HubSpot contact, or whose HubSpot identity needs review. Correct each one from the person's HubSpot page.",
        stops: [
            stop(
                hubspotTourTargets.summary,
                "Summary",
                "Counts of active and inactive members, matched personnel, new active members and conflicts, across all members."
            ),
            grid("Each row is one member with an issue; select the person to reconcile them.", [
                column("HubSpot member", "The member's name in HubSpot."),
                column("Email", "The member's email address in HubSpot."),
                column("Contact type", "The kind of HubSpot contact, such as a member."),
                column("Membership", "The member's TROG membership type and state in HubSpot."),
                column(
                    "Personnel",
                    "The person in this system the member is matched to, if any; select it to open their HubSpot page."
                ),
                column(
                    "Matched by",
                    "How the member was matched to the person, such as by email or HubSpot identity."
                ),
                column(
                    "Issue",
                    "Why the member needs attention, such as an email difference or a conflicting match."
                )
            ])
        ],
        notes: [
            "Process membership, on the Active Members page, reprocesses every member once issues are resolved."
        ]
    },
    "/settings/openclinicauserroles": {
        intro: "The OpenClinica user roles created from personnel roles, and whether each was created successfully in OpenClinica.",
        stops: [
            filterStop(
                "Filter by person, trial, institution or status to find failed or pending roles."
            ),
            actionsStop(
                "Discovery Preview compares OpenClinica with this system and shows what would be created or corrected, before anything changes. Synchronize now rebuilds the list from personnel roles and their OpenClinica mappings, and reports the counts.",
                Permission.OpenClinicaUserRolesUpdate
            ),
            grid("Each row is one role in one OpenClinica study.", [
                column("Personnel", "The person the OpenClinica role is for."),
                column("Trial", "The trial whose personnel role created it."),
                column("Institution", "The site the role is at, for site-level roles."),
                column(
                    "OC Role",
                    "The OpenClinica role, taken from the personnel role's OpenClinica mapping."
                ),
                column("Study", "The OpenClinica study (trial or site)."),
                column(
                    "Status",
                    "Whether the role has been set up in OpenClinica, is waiting, or failed."
                ),
                column("Error", "Why the last attempt to set up the role failed, if it did."),
                column(
                    "Actions",
                    "Synchronise the role with OpenClinica again, for example after correcting the cause of an error."
                )
            ])
        ]
    },

    ...categoryGuides({
        list: "/settings/institution/capability-categories",
        item: "/settings/institution/capability-categories/:institutionCapabilityCategoryId",
        create: "/settings/institution/capability-categories/create",
        plural: "capability categories",
        singular: "capability category",
        purpose:
            "Capability categories group institution capabilities, such as techniques or equipment, and colour them.",
        createPermission: Permission.InstitutionCapabilityCategoriesCreate,
        updatePermission: Permission.InstitutionCapabilityCategoriesUpdate,
        effect: "Renaming or recolouring a category changes it on every trial and site that shows its capabilities."
    }),
    ...catalogueGuides({
        list: "/settings/institution/capabilities",
        item: "/settings/institution/capabilities/:institutionCapabilityId",
        create: "/settings/institution/capabilities/create",
        plural: "capabilities",
        singular: "capability",
        purpose:
            "A capability is something a site can offer, such as a treatment technique or equipment. Trials require or permit capabilities, and they decide which items apply to a site.",
        categoryPath: "institutionCapabilityCategoryId",
        createPermission: Permission.InstitutionCapabilitiesCreate,
        updatePermission: Permission.InstitutionCapabilitiesUpdate,
        effect: "Changes show on every trial and site that uses the capability."
    }),
    ...categoryGuides({
        list: "/settings/institution/tag-categories",
        item: "/settings/institution/tag-categories/:institutionTagCategoryId",
        create: "/settings/institution/tag-categories/create",
        plural: "institution tag categories",
        singular: "institution tag category",
        purpose: "Tag categories group the tags used to describe trial sites, and colour them.",
        createPermission: Permission.InstitutionTagCategoriesCreate,
        updatePermission: Permission.InstitutionTagCategoriesUpdate,
        effect: "Renaming or recolouring a category changes it wherever its tags are shown."
    }),
    ...catalogueGuides({
        list: "/settings/institution/tags",
        item: "/settings/institution/tags/:institutionTagId",
        create: "/settings/institution/tags/create",
        plural: "institution tags",
        singular: "institution tag",
        purpose:
            "Institution tags describe and group trial sites, for example by region or network. Trials choose the tags they use, and tags can decide which items apply to a site.",
        categoryPath: "institutionTagCategoryId",
        createPermission: Permission.InstitutionTagsCreate,
        updatePermission: Permission.InstitutionTagsUpdate,
        effect: "Changes show on every trial that uses the tag."
    }),
    ...categoryGuides({
        list: "/settings/participant/tag-categories",
        item: "/settings/participant/tag-categories/:patientTagCategoryId",
        create: "/settings/participant/tag-categories/create",
        plural: "participant tag categories",
        singular: "participant tag category",
        purpose:
            "Participant tag categories group the tags used to classify participants, and colour them.",
        createPermission: Permission.PatientTagCategoriesCreate,
        updatePermission: Permission.PatientTagCategoriesUpdate,
        effect: "Renaming or recolouring a category changes it wherever its tags are shown."
    }),
    ...catalogueGuides({
        list: "/settings/participant/tags",
        item: "/settings/participant/tags/:patientTagId",
        create: "/settings/participant/tags/create",
        plural: "participant tags",
        singular: "participant tag",
        purpose:
            "Participant tags classify participants, for example by cohort or sub-study. Trials choose the tags they use, and tags can decide which participant items apply.",
        categoryPath: "patientTagCategoryId",
        createPermission: Permission.PatientTagsCreate,
        updatePermission: Permission.PatientTagsUpdate,
        effect: "Changes show on every participant with the tag."
    }),

    // QA settings
    "/settings/qa/formtemplates": {
        intro: "The QA form templates: the audits, checklists and questionnaires completed by trials, institutions and participants.",
        stops: [
            filterStop(
                "Type to filter by name, type or category; switch on inactive records to include retired templates."
            ),
            actionsStop(
                "Create opens a blank form template; build its layout once it is saved.",
                Permission.FormTemplatesCreate
            ),
            grid("Each row is one template; select it to change it and its layout.", [
                column(
                    "Form Type",
                    "What the form is, such as a facility questionnaire, audit or checklist."
                ),
                column("Category", "How templates are grouped."),
                column("Description", "What the form covers and when it is completed.")
            ])
        ]
    },
    "/settings/qa/formtemplates/:formTemplateId": {
        intro: "Build a QA form template. Trial and institution forms are created from it.",
        stops: [
            field(
                "name",
                "Name",
                "The form's name, shown when forms are created from it and in form lists."
            ),
            field("description", "Description", "What the form covers and when it is completed."),
            field(
                "rank",
                "Rank",
                "The order the template is listed in; lower numbers are listed first."
            ),
            field(
                "itemComponentTypeId",
                "Form Type",
                "What kind of form it is. The type decides where the form can be used."
            ),
            field(
                "formTemplateCategoryId",
                "Category",
                "The form template category, which groups templates in lists and when forms are created."
            ),
            field(
                "active",
                "Active",
                "Clear to stop new forms being created from it. Existing forms are unaffected."
            ),
            field(
                "requiresReview",
                "Requires Review",
                "When enabled, submitted forms wait for review before being marked Complete. Otherwise, they become Complete on submission."
            ),
            ...builderStops(Permission.FormTemplatesUpdate),
            saveStop(
                "Submit saves the template and its layout; Submit And Return also goes back to the list.",
                Permission.FormTemplatesUpdate
            )
        ]
    },
    "/settings/qa/program-checklist": {
        intro: "The default categories and tasks of the QA program checklist. Trials copy these defaults into their own checklist and then manage their copy.",
        stops: [
            stop(
                helpTourTargets.pageActions,
                "Adding defaults",
                "Add Category adds a default category; expand a category below to add, edit or reorder its tasks.",
                Permission.QaProgramChecklistCategoriesCreate
            )
        ],
        notes: [
            "Existing trial checklists are not changed; trials pick up new defaults when they import them."
        ]
    },
    "/settings/qa/qaEmailTemplates": {
        intro: "The email templates used for the emails the system sends, such as overdue form reminders and the assigned, requires review and complete emails for items and forms.",
        stops: [
            filterStop("Type part of a subject or body to filter the list."),
            actionsStop(
                "Create opens a blank QA email template for one of the QA emails.",
                Permission.QAEmailTemplatesCreate
            ),
            grid("Each row is one template; select it to change or preview it.", [
                column("Type", "Which email the template is for, such as Trial Item Assigned."),
                column("Subject", "The email's subject line."),
                column("Body", "The start of the message.")
            ])
        ]
    },
    "/settings/qa/qaEmailTemplates/:standardEmailTemplateId": emailTemplate,
    "/settings/qa/qaEmailTemplates/create": createGuide(
        emailTemplate,
        "Add a QA email template. Choose the email it is for, then write its subject and body with merge fields.",
        ["Once saved, the template is used the next time that email is sent."]
    ),
    "/settings/qa/formcomponents": {
        intro: "The reusable components QA form templates are built from, such as questions, measurements and file uploads.",
        stops: [
            filterStop("Type to filter by name, description or type."),
            actionsStop(
                "Add opens the component builder with a blank component.",
                Permission.FormComponentsCreate
            ),
            grid("Each row is one component; select it to change its inputs and rules.", [
                column("Component Name", "The name used when adding it to a form."),
                column("Description", "What the component asks for."),
                column("Component Type", "The kind of form the component is for.")
            ])
        ]
    },
    "/settings/qa/formcomponents/:itemComponentId": componentBuilder(
        "QA form component",
        "QA form templates",
        Permission.FormComponentsUpdate
    ),
    "/settings/qa/formcomponents/create": createGuide(
        componentBuilder("QA form component", "QA form templates", Permission.FormComponentsCreate),
        "Add a QA form component. Choose its type first; the options below change with it.",
        ["Once saved, the component can be added to QA form templates."]
    ),

    // Item settings
    "/settings/item/itemcomponents": {
        intro: "The reusable components item templates are built from, such as dates, choices, notes and file uploads.",
        stops: [
            filterStop("Type to filter by name, description or type."),
            actionsStop(
                "Add opens the component builder with a blank component.",
                Permission.ItemComponentsCreate
            ),
            grid("Each row is one component; select it to change its inputs and rules.", [
                column("Component Name", "The name used when adding it to a template."),
                column("Description", "What the component asks for."),
                column("Component Type", "The kind of item the component is for.")
            ])
        ]
    },
    "/settings/item/itemcomponents/:itemComponentId": componentBuilder(
        "item component",
        "item templates",
        Permission.ItemComponentsUpdate
    ),
    "/settings/item/itemcomponents/create": createGuide(
        componentBuilder("item component", "item templates", Permission.ItemComponentsCreate),
        "Add an item component. Choose its type first; the options below change with it.",
        ["Once saved, the component can be added to item templates."]
    ),
    ...templateGuides(
        {
            list: "/settings/item/itemtemplates",
            item: "/settings/item/itemtemplates/:trialInstitutionItemTemplateId",
            create: "/settings/item/itemtemplates/create"
        },
        {
            plural: "trial institution item templates",
            singular: "trial institution item template",
            purpose:
                "These define the items each trial site completes, such as agreements and approvals. Trials import them from here.",
            versionPath: "allowVersions",
            withRank: false,
            createPermission: Permission.TrialInstitutionItemTemplatesCreate,
            updatePermission: Permission.TrialInstitutionItemTemplatesUpdate
        }
    ),
    ...templateGuides(
        {
            list: "/settings/item/participant-item-templates",
            item: "/settings/item/participant-item-templates/:patientItemTemplateId",
            create: "/settings/item/participant-item-templates/create"
        },
        {
            plural: "participant item templates",
            singular: "participant item template",
            purpose:
                "These define the items collected for each participant. Trials import them from here.",
            versionPath: "allowVersions",
            withRank: false,
            createPermission: Permission.PatientItemTemplatesCreate,
            updatePermission: Permission.PatientItemTemplatesUpdate
        }
    ),
    ...templateGuides(
        {
            list: "/settings/item/trial-item-templates",
            item: "/settings/item/trial-item-templates/:trialItemTemplateId",
            create: "/settings/item/trial-item-templates/create"
        },
        {
            plural: "trial item templates",
            singular: "trial item template",
            purpose:
                "These define trial-level items, such as protocol documents and approvals. Trials create items from them.",
            versionPath: "allowIntraVersions",
            withRank: true,
            createPermission: Permission.TrialItemTemplatesCreate,
            updatePermission: Permission.TrialItemTemplatesUpdate
        }
    )
};

settingsPageGuides["/settings/general/notices/create"] = createGuide(
    settingsPageGuides["/settings/general/notices/:noticeId"],
    "Add a notice. Write the title and message, choose how it is displayed and who sees it, then set when it starts.",
    [
        "Submit publishes the notice from its start date; users see it the next time their notices refresh."
    ],
    { omit: ["active"] }
);
settingsPageGuides["/settings/qa/formtemplates/create"] = createGuide(
    settingsPageGuides["/settings/qa/formtemplates/:formTemplateId"],
    "Add a QA form template. Name it, choose its form type and category, then build its sections and components.",
    ["Once saved, the template can be used to create trial and institution forms."],
    { omit: ["active"] }
);

export default settingsPageGuides;
